import { hasOptedOutNavRefresh } from '@townsquare/nav-refresh-opt-out/has-opted-out-nav-refresh';
import { ResourceContext } from '@townsquare/router/types';
import { createSearchParamsFromCurrentUrl } from '@townsquare/url-utils/search-params';
import { useWorkspaceType } from '@townsquare/workspace-store/hooks';
import { WorkspaceDetails } from '@townsquare/workspace-store/types';

import { isGateEnabled } from './Gate';

type ExpectedResourceContext = Pick<ResourceContext, 'workspace'>;

const GRIFFIN_NAV_OPT_OUT = 'nav4-optout';

const isQueryParamOptOut = (): boolean => {
  const param = createSearchParamsFromCurrentUrl().get(GRIFFIN_NAV_OPT_OUT);

  try {
    if (param && JSON.parse(param) === true) {
      return true;
    }
  } catch {
    // Don't let parsing blow up the app.
  }
  return false;
};

const hasOptedOut = hasOptedOutNavRefresh() || isQueryParamOptOut();
const isAtlassianHome = (workspaceType: WorkspaceDetails['type'] | undefined) => workspaceType !== 'PRODUCT';

export const useIsNavRefreshEnabled = ({ considerOptOut = true } = {}) => {
  const workspaceType = useWorkspaceType();
  const optedOut = considerOptOut && hasOptedOut;

  return shouldEnableNavRefresh(workspaceType, optedOut);
};

export const isNavRefreshEnabled = (resourceContext: ExpectedResourceContext, { considerOptOut = true } = {}) => {
  const optedOut = considerOptOut && hasOptedOut;

  return shouldEnableNavRefresh(resourceContext.workspace?.type, optedOut);
};

const shouldEnableNavRefresh = (workspaceType: WorkspaceDetails['type'] | undefined, optedOut: boolean) => {
  const enableNavRefreshMasterSwitch = isGateEnabled('jira_nav4');
  const enableNavRefreshDogfooding = isGateEnabled('jira_nav4_dogfooding');
  const enableNavRefreshEAP = isGateEnabled('jira_nav4_eap');

  return (
    isAtlassianHome(workspaceType) && // Only Atlassian Home
    !optedOut && // User can choose to opt-out of nav4
    enableNavRefreshMasterSwitch && // If master switch is off, everything is off
    (enableNavRefreshDogfooding || enableNavRefreshEAP) // Get nav4 as long as user is enrolled into either dogfood or EAP
  );
};

export const useIsHomeVisualRefreshEnabled = () => {
  const workspaceType = useWorkspaceType();
  return shouldEnableVisualRefresh(workspaceType);
};

export const shouldEnableVisualRefresh = (workspaceType: WorkspaceDetails['type'] | undefined) => {
  const enableHomeVisualRefresh = isGateEnabled('atlassian_home_visual_refresh');

  return (
    isAtlassianHome(workspaceType) && // Only Atlassian Home
    shouldEnableNavRefresh(workspaceType, hasOptedOut) && // Only for nav4 users
    enableHomeVisualRefresh
  );
};
